import { fetchConfig } from "@theme/utilities";
import { ThemeEvents, CartUpdateEvent } from "@theme/events";
import { morphSection } from "@theme/section-renderer";

const SECTION_ID = "auto-free-gift";

class AutoFreeGift extends HTMLElement {
  #syncing = false;
  #queued = false;

  connectedCallback() {
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    this.sync(this.#readState(this));
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }

  handleCartUpdate(event) {
    if (event.detail?.data?.source === "auto-free-gift") return;
    this.sync();
  }

  get #giftVariantId() {
    return Number(this.dataset.giftVariantId);
  }

  #readState(el) {
    return {
      hasTrigger: el?.dataset.hasTrigger === "true",
      giftKey: el?.dataset.giftLineKey || "",
      giftQty: Number(el?.dataset.giftQty || 0),
    };
  }

  async #fetchState() {
    const res = await fetch(`${window.location.pathname}?sections=${SECTION_ID}`);
    const data = await res.json();
    const doc = new DOMParser().parseFromString(data[SECTION_ID], "text/html");
    return this.#readState(doc.querySelector("auto-free-gift"));
  }

  async sync(known) {
    if (this.#syncing) {
      this.#queued = true;
      return;
    }
    this.#syncing = true;

    try {
      const state = known || (await this.#fetchState());

      if (state.hasTrigger && !state.giftKey) {
        await this.#addGift();
      } else if (!state.hasTrigger && state.giftKey) {
        await this.#changeLine(state.giftKey, 0);
      } else if (state.hasTrigger && state.giftKey && state.giftQty !== 1) {
        await this.#changeLine(state.giftKey, 1);
      }
    } catch (error) {
      console.error("[auto-free-gift]", error);
    } finally {
      this.#syncing = false;
      if (this.#queued) {
        this.#queued = false;
        this.sync();
      }
    }
  }

  #sectionPayload() {
    const ids = [SECTION_ID];
    document.querySelectorAll("cart-items-component").forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return { sections: ids.join(","), sections_url: window.location.pathname };
  }

  async #addGift() {
    const body = JSON.stringify({
      items: [{ id: this.#giftVariantId, quantity: 1, properties: { _free_gift: "true" } }],
      ...this.#sectionPayload(),
    });
    const res = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", { body }));
    await this.#applyResponse(await res.json());
  }

  async #changeLine(key, quantity) {
    const body = JSON.stringify({ id: key, quantity, ...this.#sectionPayload() });
    const res = await fetch(FoxTheme.routes.cart_change_url, fetchConfig("json", { body }));
    await this.#applyResponse(await res.json());
  }

  async #applyResponse(parsed) {
    if (!parsed || parsed.status) {
      if (parsed?.message) console.error("[auto-free-gift]", parsed.message);
      return;
    }
    const sections = parsed.sections;
    if (!sections) return;

    if (sections[SECTION_ID]) {
      const doc = new DOMParser().parseFromString(sections[SECTION_ID], "text/html");
      const fresh = doc.querySelector("auto-free-gift");
      if (fresh) {
        for (const key of ["hasTrigger", "giftLineKey", "giftQty"]) {
          this.dataset[key] = fresh.dataset[key] || "";
        }
      }
    }

    let itemCount = 0;
    for (const sectionId of Object.keys(sections)) {
      if (sectionId === SECTION_ID) continue;
      await morphSection(sectionId, sections[sectionId]);
      const doc = new DOMParser().parseFromString(sections[sectionId], "text/html");
      const countEl = doc.querySelector('[ref="cartItemCount"]');
      if (countEl?.textContent) itemCount = parseInt(countEl.textContent, 10) || itemCount;
    }

    document.querySelectorAll("cart-items-component").forEach((comp) => {
      const sid = comp.dataset.sectionId;
      if (sid && sections[sid]) {
        comp.dispatchEvent(
          new CartUpdateEvent({}, sid, { itemCount, source: "auto-free-gift", sections })
        );
      }
    });
  }
}

if (!customElements.get("auto-free-gift")) {
  customElements.define("auto-free-gift", AutoFreeGift);
}
